import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { errMsg } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { Check, X, AlertTriangle, ShieldCheck, Loader2, ArrowRight } from "lucide-react";

function scrollToPanel(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function Row({ ok, warn, label, hint, testId, action }) {
  const color = ok ? "var(--ok)" : warn ? "var(--orange)" : "var(--err)";
  const Icon = ok ? Check : warn ? AlertTriangle : X;
  return (
    <div data-testid={testId} className="flex items-center gap-2.5" style={{ padding: "10px 0", borderBottom: "1px solid var(--cardborder)" }}>
      <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, display: "grid", placeItems: "center", background: color }}>
        <Icon size={12} strokeWidth={3} style={{ color: "#fff" }} />
      </span>
      <span className="font-body" style={{ fontSize: 13.5, color: "var(--tprimary)", flex: 1 }}>{label}</span>
      {action ? action : <span className="font-mono" style={{ fontSize: 11, color: "var(--tmuted)" }}>{hint}</span>}
    </div>
  );
}

function ActionBtn({ onClick, busy, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className="font-mono"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, padding: "5px 11px", borderRadius: 8, border: "1px solid var(--orange)", background: "transparent", color: "var(--orange)", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}
    >
      {busy ? <Loader2 size={12} className="spin" /> : <ArrowRight size={12} />} {children}
    </button>
  );
}

export function BrandKitHealth({ brand, onGenerate }) {
  const [busy, setBusy] = useState(null);
  if (!brand) return null;

  const logo = brand.logo;
  const hasLogo = !!(logo && logo.url);
  const logoCrisp = hasLogo && !logo.lowRes;
  const colors = brand.colors || [];
  const hasPalette = colors.length >= 3;
  const hasVoice = !!brand.voiceCard;
  const pillars = brand.pillars || [];
  const hasPillars = pillars.length >= 1;

  const checks = [hasLogo, hasPalette, hasVoice, hasPillars];
  const done = checks.filter(Boolean).length;
  const ready = done === checks.length && logoCrisp;
  const pct = Math.round((done / checks.length) * 100);
  const badgeText = ready ? "Render-ready" : (done === checks.length ? "Almost ready" : `${pct}% ready`);

  const run = async (kind) => {
    setBusy(kind);
    try {
      await onGenerate?.(kind);
      toast.success(kind === "voice" ? "Voice card generated" : "Content pillars generated");
    } catch (e) {
      toast.error(errMsg(e, "Generation failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel-card" style={{ padding: 22 }} data-testid="brand-kit-health">
      <div className="flex items-start justify-between gap-3" style={{ marginBottom: 6 }}>
        <div>
          <Eyebrow testId="eyebrow-kit-health">Brand Kit Health</Eyebrow>
          <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "6px 0 0" }}>
            Everything your brand needs to generate polished, on-brand content.
          </p>
        </div>
        <span
          data-testid="kit-ready-badge"
          className="font-mono"
          style={{
            flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 999,
            display: "inline-flex", alignItems: "center", gap: 6,
            background: ready ? "var(--ok)" : "var(--panel)",
            color: ready ? "#fff" : "var(--tsecondary)",
            border: ready ? "none" : "1px solid var(--cardborder)",
          }}
        >
          <ShieldCheck size={13} /> {badgeText}
        </span>
      </div>

      <div style={{ marginTop: 12 }}>
        <Row testId="kit-logo" ok={logoCrisp} warn={hasLogo && !logoCrisp}
             label="Logo" hint={logoCrisp ? "ready" : ""}
             action={!logoCrisp ? (
               <ActionBtn testId="kit-logo-action" onClick={() => scrollToPanel("logo-panel")}>
                 {hasLogo ? "Replace" : "Upload"}
               </ActionBtn>
             ) : null} />
        <Row testId="kit-palette" ok={hasPalette}
             label="Color palette" hint={hasPalette ? `${colors.length} colors` : ""}
             action={!hasPalette ? (
               <ActionBtn testId="kit-palette-action" onClick={() => scrollToPanel("brand-palette")}>Add colors</ActionBtn>
             ) : null} />
        <Row testId="kit-voice" ok={hasVoice}
             label="Voice card" hint={hasVoice ? "ready" : ""}
             action={!hasVoice ? (
               <ActionBtn testId="kit-voice-action" busy={busy === "voice"} onClick={() => run("voice")}>Generate</ActionBtn>
             ) : null} />
        <Row testId="kit-pillars" ok={hasPillars}
             label="Content pillars" hint={hasPillars ? `${pillars.length} set` : ""}
             action={!hasPillars ? (
               <ActionBtn testId="kit-pillars-action" busy={busy === "pillars"} onClick={() => run("pillars")}>Generate</ActionBtn>
             ) : null} />
      </div>
    </div>
  );
}
